import React from 'react';
import { bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';

import EsmsDialog from '../../components/EsmsDialog';

import * as UiActions from '../../actions/uiActions';

import classes from './RefreshInventorySettingsModal.module.css';

const ValidationSchema = Yup.object().shape({
    startRange: Yup.number()
        .transform((o, v) => parseFloat(v.toString().replace(/,/g, '')))
        .typeError('Must be a number.')
        .max(4194302, 'Must be less than 4,194,302.')
        .min(0, 'Must be 0 or greater.')
        .required()
        .when('endRange', (endRange) => {
            if (endRange < 4194302) {
                return Yup.number()
                    .transform((o, v) => parseFloat(v.toString().replace(/,/g, '')))
                    .typeError('Must be a number.')
                    .max(endRange, 'Must be less than end range.')
                    .min(0, 'Must be 0 or greater.')
                    .required();
            }
        })
        .required(),
    endRange: Yup.number()
        .transform((o, v) => parseFloat(v.toString().replace(/,/g, '')))
        .typeError('Must be a number.')
        .max(4194302, 'Must be less than 4,194,302.')
        .min(1, 'Must be 1 or greater.')
        .required()
        .when('startRange', (startRange) => {
            if (startRange > 0) {
                return Yup.number()
                    .transform((o, v) => parseFloat(v.toString().replace(/,/g, '')))
                    .typeError('Must be a number.')
                    .max(4194302, 'Must be less than 4,194,302.')
                    .min(startRange, 'Must be greater than start range.')
                    .required();
            }
        })
}, ['endRange', 'startRange']);

const RefreshInventorySettingsModal = ({
    clearErrors,
    errors,
    handleRefreshInventory,
    handleSelectedAgentRoute,
    isLoading,
    onClose,
    open,
    title,
    toggleLoading,
    triggerInventoryScan,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            startRange: 0,
            endRange:   4194302
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            setSubmitting(true);

            const dateRange = {
                startRange: values.startRange,
                endRange:   values.endRange,
            };

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    handleRefreshInventory(dateRange);
                    handleClose();
                    handleSelectedAgentRoute();
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        onClose();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={formik.handleSubmit}
            onRendered={formik.handleReset}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Refresh"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/basAgentIconColor.svg" alt="settings icon" />
                        <span>Refresh Inventory Settings</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Click refresh to kick off a scan of BAS. This will be performed in the background and may take a few minutes.
                        </p>
                        <p>
                            If you'd like to narrow the range of discoverable Devices fill out the Start and End Range, otherwise leave the default.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormLabel className={classes.formLabel}>Scan Device Range</FormLabel>
                        <FormControl>
                            <InputLabel className={formik.errors.startRange && classes.errors}>Start Range</InputLabel>
                            <Input 
                                className={formik.errors.startRange ? `${classes.errors} ${classes.cssErrorUnderline} ${classes.startRangeInput}` : classes.startRangeInput}
                                name="startRange" 
                                value={formik.values.startRange}
                                onChange={formik.handleChange} 
                            />
                            {
                                formik.errors.startRange && 
                                    <FormHelperText className={classes.errors}>
                                        {formik.errors.startRange}
                                    </FormHelperText>
                            }
                        </FormControl>
                        <FormControl>
                            <InputLabel className={formik.errors.endRange && classes.errors}>End Range</InputLabel>
                            <Input 
                                className={formik.errors.endRange ? `${classes.errors} ${classes.cssErrorUnderline} ${classes.endRangeInput}` : classes.endRangeInput}
                                name="endRange"
                                value={formik.values.endRange}
                                onChange={formik.handleChange}
                            />
                            {
                                formik.errors.endRange && 
                                    <FormHelperText className={classes.errors}>
                                        {formik.errors.endRange}
                                    </FormHelperText>
                            }
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

RefreshInventorySettingsModal.defaultProps = {
    open:                     false,
    onClose:                  () => {},
    handleRefreshInventory:   () => {},
    handleSelectedAgentRoute: () => {},
    isLoading:                false,
    title:                    ''
};

RefreshInventorySettingsModal.propTypes = {
    onClose:                  func.isRequired,
    open:                     bool.isRequired,
    handleRefreshInventory:   func.isRequired,
    handleSelectedAgentRoute: func.isRequired,
    isLoading:                bool.isRequired,
    title:                    string.isRequired
};

const mapStateToProps = state => {
    return {
        isLoading: state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(RefreshInventorySettingsModal);

